import classNames from 'classnames'
import { useState } from 'react'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { TextField } from '../forms/text-field/text-field.js'
import './working-pattern-day-grid.css'

export interface WorkingPatternDayLabels {
    mon: string
    tue: string
    wed: string
    thu: string
    fri: string
    sat: string
    sun: string
}

interface WorkingPatternDayGridProps {
    /** `fixed` renders per-day hour inputs; `flex` renders on/off day toggles. */
    mode: 'fixed' | 'flex'
    /** Length-7 Mon-first hours array; `0` means the day is off. */
    days: number[]
    onChange: (days: number[]) => void
    dayLabels: WorkingPatternDayLabels
    /** Hours written into a day when it is toggled on in `flex` mode. */
    standardDayHours?: number
    /** Non-editable preview (e.g. an existing pattern shown for reference). */
    readOnly?: boolean
}

/**
 * Parse a typed day-hours entry. Accepts both decimal separators ("3.5" and
 * the Estonian "3,5"); empty means an off day. Out-of-range or non-numeric
 * input returns null — the caller keeps the previous value.
 */
const parseDayHours = (raw: string): number | null => {
    if (raw.trim() === '') return 0
    const n = Number(raw.replace(',', '.'))
    return Number.isFinite(n) && n >= 0 && n <= 24 ? n : null
}

const orderedLabels = (l: WorkingPatternDayLabels): string[] => [
    l.mon,
    l.tue,
    l.wed,
    l.thu,
    l.fri,
    l.sat,
    l.sun,
]

/**
 * The Humaans-style weekly working-hours grid: seven Mon-first day columns,
 * each an editable hours box (fixed) or an on/off toggle (flex). Shared by the
 * workspace-settings pattern editor and the profile "Muuda mustrit" flow so the
 * two surfaces stay visually identical. Internal representation is always a
 * numeric array; callers convert to booleans for flex at submit time.
 */
export const WorkingPatternDayGrid = ({
    mode,
    days,
    onChange,
    dayLabels,
    standardDayHours = 8,
    readOnly = false,
}: WorkingPatternDayGridProps) => {
    // Raw text per cell while it is being typed in — a controlled numeric
    // value would eat partial decimals ("3." / "3,") on every keystroke.
    // Cleared on blur so the display snaps back to the committed number.
    const [drafts, setDrafts] = useState<Record<number, string>>({})

    const setDay = (index: number, value: number) => {
        const next = [...days]
        next[index] = value
        onChange(next)
    }

    const handleDayInput = (index: number, raw: string) => {
        setDrafts((d) => ({ ...d, [index]: raw }))
        const parsed = parseDayHours(raw)
        if (parsed !== null) setDay(index, parsed)
    }

    const clearDraft = (index: number) => {
        setDrafts(({ [index]: _dropped, ...rest }) => rest)
    }

    return (
        <div className="working-pattern-grid">
            {orderedLabels(dayLabels).map((label, i) => {
                const value = days[i] ?? 0
                return (
                    <div key={i} className="working-pattern-grid__day">
                        <div className="working-pattern-grid__label">{label}</div>
                        {mode === 'flex' ? (
                            <button
                                type="button"
                                aria-pressed={value > 0}
                                disabled={readOnly}
                                className={classNames(
                                    'working-pattern-grid__toggle',
                                    value > 0 && 'working-pattern-grid__toggle--on',
                                )}
                                onClick={() => setDay(i, value > 0 ? 0 : standardDayHours)}
                            >
                                {value > 0 ? '✓' : '–'}
                            </button>
                        ) : (
                            <TextField
                                inputClass="working-pattern-grid__input"
                                inputProps={{ inputMode: 'decimal', onBlur: () => clearDraft(i) }}
                                value={drafts[i] ?? String(value)}
                                isDisabled={readOnly}
                                onChange={(v) => handleDayInput(i, v)}
                            />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

interface WorkingPatternFtePreviewProps {
    workingHours: number
    fullTimeHours: number
    daysPerWeek: number
    lang: Language
}

const roundHours = (n: number): number => Math.round(n * 100) / 100

/** The "1 FTE / 40h/week, 5 days a week" summary card from the Humaans editor. */
export const WorkingPatternFtePreview = ({
    workingHours,
    fullTimeHours,
    daysPerWeek,
    lang,
}: WorkingPatternFtePreviewProps) => {
    const fteRaw = fullTimeHours > 0 ? Math.min(1, workingHours / fullTimeHours) : 0
    const fte = fteRaw === 1 ? '1' : fteRaw.toFixed(2)
    const s = t.portal.settings.workingPatterns
    return (
        <div className="working-pattern-fte">
            <div className="working-pattern-fte__value">{fte} FTE</div>
            <div className="working-pattern-fte__sub">
                {s.workingSummary(String(roundHours(workingHours)), String(daysPerWeek))(lang)}
            </div>
        </div>
    )
}
